import Card from "@mui/material/Card";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import { useState } from "react";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import CircularProgress from "@mui/material/CircularProgress";
import SupportTicketCategoryTableData from "Views/UsefulCorners/SupportTicketCategory/data/SupportTicketCategoryTableData";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import axios from "utils/api";
import { baseURL } from "utils/api";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";

function SupportTicketCategory() {
  const [refresh, setRefresh] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Add/Edit form dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add"); // 'add' | 'edit'
  const [editId, setEditId] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  // Delete button click handler
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDeleteDialog(false);
    setDeleteId(null); // reset selected id
  };

  // Open Add form
  const handleAddClick = () => {
    setFormMode("add");
    setCategoryName("");
    setEditId(null);
    setFormOpen(true);
  };

  // Open Edit form from table
  const handleEditClick = (id, name) => {
    setFormMode("edit");
    setEditId(id);
    setCategoryName(name || "");
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setFormLoading(false);
    setCategoryName("");
    setEditId(null);
  };

  // Submit Add/Edit
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!categoryName.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }
    setFormLoading(true);
    if (formMode === "add") {
      axios
        .post(`${baseURL}/support-ticket-categories`, { name: categoryName })
        .then((res) => {
          toast.success(res.data?.message || "Support ticket category added successfully");
          setRefresh((prev) => !prev);
          handleCloseForm();
        })
        .catch((err) => {
          toast.error(err.response?.data?.message || "Failed to add support ticket category");
        })
        .finally(() => setFormLoading(false));
    } else {
      axios
        .put(`${baseURL}/support-ticket-categories/${editId}`, { name: categoryName })
        .then((res) => {
          toast.success(res.data?.message || "Support ticket category updated successfully");
          setRefresh((prev) => !prev);
          handleCloseForm();
        })
        .catch((err) => {
          toast.error(err.response?.data?.message || "Failed to update support ticket category");
        })
        .finally(() => setFormLoading(false));
    }
  };

  const handleConfirmDelete = () => {
    axios
      .delete(`${baseURL}/support-ticket-categories/${deleteId}`)
      .then((res) => {
        toast.success(res.data?.message || "Support ticket category deleted successfully");
        setRefresh((prev) => !prev);
        handleCloseDialog();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Failed to delete support ticket category");
      });
  };

  const { columns, rows, count, filteredCategories } = SupportTicketCategoryTableData(
    handleDeleteClick,
    handleEditClick,
    refresh,
    searchQuery
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={2}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" p={1}>
                <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                  All Support Ticket Categories {!loading && `(${count})`}
                </ArgonTypography>
                <ArgonBox display="flex" alignItems="center">
                  <ArgonInput
                    placeholder="search"
                    name="search"
                    size="small"
                    type="text"
                    variant="outlined"
                    sx={{ margin: "10px" }}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <ArgonButton
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={handleAddClick}
                  >
                    Add
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <CircularProgress color="info" />
                </ArgonBox>
              ) : rows.length === 0 ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <ArgonTypography variant="body2" color="text">
                    {searchQuery
                      ? "No support ticket categories found matching your search"
                      : "No support ticket categories available"}
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rows} />
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={deleteDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Support Ticket Category"
        description="Are you sure you want to delete this support ticket category?"
        confirmText="Delete Category"
        cancelText="Cancel"
      />

      {/* Add/Edit Form Dialog */}
      <Dialog
        open={formOpen}
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
          },
        }}
      >
        <DialogTitle>
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
            <ArgonTypography variant="h5" fontWeight="medium">
              {formMode === "add"
                ? "Add New Support Ticket Category"
                : "Edit Support Ticket Category"}
            </ArgonTypography>
            <IconButton onClick={handleCloseForm} size="small">
              <Close />
            </IconButton>
          </ArgonBox>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            <ArgonBox mb={2}>
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                Category Name *
              </ArgonTypography>
              <ArgonInput
                type="text"
                placeholder="Enter category name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                fullWidth
                required
              />
            </ArgonBox>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2 }}>
            <ArgonButton
              variant="outlined"
              color="secondary"
              onClick={handleCloseForm}
              disabled={formLoading}
            >
              Cancel
            </ArgonButton>
            <ArgonButton type="submit" variant="gradient" color="info" disabled={formLoading}>
              {formLoading
                ? formMode === "add"
                  ? "Adding..."
                  : "Updating..."
                : formMode === "add"
                ? "Add Category"
                : "Update Category"}
            </ArgonButton>
          </DialogActions>
        </form>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default SupportTicketCategory;
